<script setup lang="ts">
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinDescriptionIdCard } from "~/modules/dtwin/models/DtwinDescriptionIdCard.interface";
import type { DynamicUIDetail } from "~/modules/dtwin/models/DynamicUIDetail.interface";
import { useConfirmLeavePageStore } from "~/stores/useConfirmLeavePageStore";

interface Props {
  modelValue: DtwinDescriptionIdCard;
  isAllowedToEdit: boolean;
  dtwin: Dtwin;
  deviceModelUiConfig: Record<string, DynamicUIDetail[]>;
}
const props = defineProps<Props>();
const emit = defineEmits(["update:name"]);

const { t } = useI18n();

const nameFieldRef = ref();
const name: Ref<string> = ref(props.modelValue.friendlyName);
const isEditing: Ref<boolean> = ref(false);
const isSending: Ref<boolean> = ref(false);
const isEdited: ComputedRef<boolean> = computed(() =>
  nameFieldRef.value && nameFieldRef.value[0].hasValueChanged
    ? nameFieldRef.value[0].hasValueChanged
    : false,
);

// When the request to the backend succeed, reset the editing flag to true
watch(
  () => props.modelValue,
  (modelValue) => {
    if (modelValue.doesUpdateSucceed) {
      isEditing.value = false;
      name.value = modelValue.friendlyName;
    }
    isSending.value = false;
  },
);

function onSave(newValue: string): void {
  isSending.value = true;
  emit("update:name", newValue);
}

// activate the leave page confirm message if edited
watch(
  () => isEdited.value,
  (isEdited) => useConfirmLeavePageStore().activate(isEdited),
);
</script>

<template>
  <block-container :title="t(`dtwins.form.description.idCard.title`)">
    <template #left>
      <template v-for="item in deviceModelUiConfig.left" :key="item.id">
        <template v-if="item.id === 'attributes.friendlyName'">
          <editable-field
            v-if="item.isAllowedToEdit && props.isAllowedToEdit"
            ref="nameFieldRef"
            v-model:is-editing="isEditing"
            :field-value="name"
            :label="t('dtwins.form.description.idCard.attributes.name')"
            :is-saving="isSending"
            class="mb-4"
            @validate="onSave"
          />
          <lxc-form-item
            v-else
            :label="t('dtwins.form.description.idCard.attributes.name')"
            read-only
          >
            <lxc-input :value="name" type="text" />
          </lxc-form-item>
        </template>
        <dtwin-form-field v-else :item="item" :dtwin="dtwin" />
      </template>
    </template>
    <template #right>
      <dtwin-form-field
        v-for="item in deviceModelUiConfig.right"
        :item="item"
        :dtwin="dtwin"
        :key="item.id"
      />
    </template>
  </block-container>
</template>
